import { useContext } from 'react';
import { bookingStepDependencies } from '..';
import useBookingChangeStep from '../../../Hooks/use-booking-change-step';
import Step from '../../../ui-components/Step';
import { bookingState } from '../client-state/booking-state-context';
import useLocalize from '../../../Hooks/useLocalize';
import CustomerFormHelpTextWithData from './customer-form-help-text-with-data';
import LastStepInfoWithData from './last-step-info-with-data';
import ItemCategoriesWithData from './item-categories-with-data';
import ItemsWithData from './items-with-data';
import useBookingShop from '../server-data/use-booking-shop';
import { Button } from '../../../SubComponents/Buttons';
import CustomerFormWithDataVariant from './customer-form-with-data-variant';
import { useMediaQuery } from 'react-responsive';
import { isSummaryOpenActionTypes } from '../../../redusers/is-summary-open-reducer';
import ConfirmBookingButton from '../../../ui-components/confirm-button';
import styled from 'styled-components/macro';
import useBookingTracking from 'Hooks/useBookingTracking';

const CustomerFormStep = () => {
  const { data: shop } = useBookingShop();
  const { state, dispatch } = useContext(bookingState);
  const localize = useLocalize();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const track = useBookingTracking();

  const shopHasItems = shop?.items && shop?.items?.length > 0;
  const numberOfSteps = shopHasItems ? 4 : 3;

  return (
    <>
      <CustomerFormHelpTextWithData />

      <CustomerFormWithDataVariant />

      <LastStepInfoWithData />

      {isMobile && (
        <ConfirmBookingButton
          dependencies={[
            { key: 'customer.formValid', value: true },
            { key: 'currentStep', value: numberOfSteps },
          ]}
          onClick={() => {
            dispatch({
              type: isSummaryOpenActionTypes.SET_IS_OPEN,
              payload: true,
            });

            track({ type: 'CHECKOUT', product: state.booking, step: 1 });
            track({ type: 'ADD_SHIPPING_INFO', product: state.booking });
          }}
        >
          {`${localize('product.goToCheckout')}`}
        </ConfirmBookingButton>
      )}
    </>
  );
};

const ItemButton = styled(Button)`
  align-self: center;

  @media screen and (min-width: 768px) {
    margin-top: 32px;
  }
`;

const ItemsStep = () => {
  const { state } = useContext(bookingState);
  const localize = useLocalize();
  const changeStep = useBookingChangeStep(bookingStepDependencies);
  const track = useBookingTracking();

  return (
    <>
      <ItemCategoriesWithData />

      <ItemsWithData />

      <ItemButton
        id="step3NextButton"
        onClick={() => {
          if (state.booking.date) {
            changeStep(4);

            track({ type: 'GO_TO_CUSTOMER_FORM_PAGE' });
          }
        }}
        className={!state.booking.date && 'disabled'}
        look="secondary"
        size="big"
      >
        {localize('product.next')}
      </ItemButton>
    </>
  );
};

// This is the new step 3 and 4 which we are testing (aka the variant), extracted to it's own component
const Step3And4WithDataVariant = () => {
  const { data: shop } = useBookingShop();
  const { state } = useContext(bookingState);
  const localize = useLocalize();
  const changeStep = useBookingChangeStep(bookingStepDependencies);
  const track = useBookingTracking();

  const shopHasNoItems = shop?.items && shop?.items?.length === 0;
  const shopHasItems = shop?.items && shop?.items?.length > 0;

  // If shop has items:
  // Items is step 3
  // Customer form is step 4

  // If shop has no items:
  // Customer form is step 3
  // No step 4

  return (
    <>
      <Step
        onClick={() => {
          changeStep(3, { onlyBackward: true });
          track({ type: 'NAVIGATE_BACK', newStep: 3 });
        }}
        currentStep={state.currentStep === 3}
        heading={`3. ${
          shopHasNoItems
            ? localize('product.yourInformation')
            : localize('product.extras')
        }`}
      >
        {shopHasNoItems ? <CustomerFormStep /> : <ItemsStep />}
      </Step>

      {!shop || shopHasItems ? (
        <Step
          onClick={() => {
            changeStep(4, { onlyBackward: true });
            track({ type: 'NAVIGATE_BACK', newStep: 4 });
          }}
          currentStep={state.currentStep === 4}
          heading={`4. ${localize('product.yourInformation')}`}
          mb="5"
        >
          <CustomerFormStep />
        </Step>
      ) : null}
    </>
  );
};

export default Step3And4WithDataVariant;
